import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'
import type { Cohort, Level } from '@/types'
import {
  Users,
  Calendar,
  Layers,
  Lock,
  Unlock,
  Settings
} from 'lucide-react'

interface CohortWithLevel extends Cohort {
  level: Level | null
}

export default function MyCohorts() {
  const { profile } = useAuth()
  const [cohorts, setCohorts] = useState<CohortWithLevel[]>([])
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    if (profile?.id) {
      loadCohorts()
    }
  }, [profile?.id])

  const loadCohorts = async () => {
    try {
      const { data: cohortsData, error: cohortsError } = await (supabase
        .from('cohorts') as any)
        .select('*')
        .eq('teacher_id', profile?.id as string)
        .order('created_at', { ascending: false })

      if (cohortsError) throw cohortsError

      const { data: levelsData } = await (supabase
        .from('levels') as any)
        .select('*')

      const cohortsWithLevel = ((cohortsData || []) as Cohort[]).map((cohort: Cohort) => ({
        ...cohort,
        level: ((levelsData || []) as Level[]).find((l: Level) => l.id === cohort.level_id) || null
      }))

      setCohorts(cohortsWithLevel)
    } catch (error) {
      console.error('Error loading cohorts:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const formatDate = (dateStr: string) => {
    return new Date(dateStr + 'T12:00:00').toLocaleDateString('es-ES', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    })
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Mis Cohortes</h1>
        <p className="text-gray-600">Gestiona tus cohortes asignadas y desbloquea unidades para tus estudiantes</p>
      </div>

      {/* Cohorts Grid */}
      {cohorts.length > 0 ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {cohorts.map((cohort) => (
            <div key={cohort.id} className="bg-white rounded-xl shadow-sm overflow-hidden">
              <div className="p-5">
                {/* Header */}
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-green-100">
                      <Users className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">{cohort.name}</h3>
                      <p className="text-xs text-gray-500">{cohort.description}</p>
                    </div>
                  </div>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                    cohort.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                  }`}>
                    {cohort.is_active ? 'Activa' : 'Inactiva'}
                  </span>
                </div>

                {/* Info */}
                <div className="space-y-2 mb-4">
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Layers className="w-4 h-4" />
                    <span>Nivel: {cohort.level?.name || 'Sin asignar'}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Users className="w-4 h-4" />
                    <span>Estudiantes: {cohort.student_ids?.length || 0} / {cohort.max_students}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Calendar className="w-4 h-4" />
                    <span>Inicio: {formatDate(cohort.start_date)}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    {cohort.unlocked_unit_index > 0 ? (
                      <Unlock className="w-4 h-4 text-green-600" />
                    ) : (
                      <Lock className="w-4 h-4 text-orange-500" />
                    )}
                    <span>Unidades desbloqueadas: {cohort.unlocked_unit_index + 1}</span>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 pt-3 border-t">
                  <Link
                    to={`/teacher/cohorts/${cohort.id}`}
                    className="flex-1 flex items-center justify-center gap-1 px-3 py-2 text-sm text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors"
                  >
                    <Settings className="w-4 h-4" />
                    Gestionar
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm p-12 text-center">
          <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">No tienes cohortes asignadas</p>
          <p className="text-sm text-gray-400 mt-1">
            Contacta al administrador para que te asigne una cohorte
          </p>
        </div>
      )}
    </div>
  )
}
